import { useEffect, Component } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import { HelmetProviderWrapper } from "./components/Seo";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import OurGroup from "./pages/OurGroup";
import Contact from "./pages/Contact";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Industries from "./pages/Industries";
import TechnologySecurity from "./pages/TechnologySecurity";
import Insights from "./pages/Insights";
import InsightArticle from "./pages/InsightArticle";
import Legal from "./pages/Legal";
import NotFound from "./pages/NotFound";
import Search from "./pages/Search";

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error) {
    console.error(error);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-charcoal flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-lime">Something went wrong</p>
            <h1 className="font-display font-bold text-3xl text-white mt-4">This section hit an unexpected error.</h1>
            <Link to="/" data-testid="error-boundary-home-link" className="mt-6 inline-block bg-lime text-charcoal font-semibold px-6 py-3">
              Back to homepage
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <HelmetProviderWrapper>
      <BrowserRouter>
        <ScrollManager />
        <SmoothScroll />
        <ErrorBoundary>
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:bg-lime focus:text-charcoal focus:px-4 focus:py-2 focus:font-semibold"
            data-testid="skip-to-content"
          >
            Skip to content
          </a>
          <Header />
          <main id="main">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/about/our-group" element={<OurGroup />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:slug" element={<ProductDetail />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/technology-security" element={<TechnologySecurity />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<InsightArticle />} />
              <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
              <Route path="/cookie-notice" element={<Legal kind="cookies" />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/search" element={<Search />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </ErrorBoundary>
      </BrowserRouter>
    </HelmetProviderWrapper>
  );
}
